import React, { useState, useMemo, useCallback } from "react";
import POEmailModal from "./POEmailModal";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "Components/Common/BreadCrumb";
import TableContainer from "Components/Common/TableContainer";
import Loader from "Components/Common/Loader";
import { Tooltip } from "react-tooltip";
import { formatDateTime } from "helpers/date_utils";
import { IsValidPermissions } from "../../../Routes/AuthProtected";
import { useGenerateReportsPDFByUuidMutation } from "slices/safetyApp/safetyappSlice";
import { useGetPurchaseOrderDownloadUidsMutation } from "slices/InventoryManagement/inventoryManagementSlice";
import { usePdfDownloader } from "Components/Hooks/usePdfDownloader";
import DownloadModal from "Components/Common/DownloadModal";
import { getToken, getUserRole } from "helpers/jwt-token-access/auth-token-header";

import {
  useGetPurchaseOrdersQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

const buildPOExportUrl = (
  base: string,
  params: {
    search?: string;
    po_status?: string;
    delivery_status?: string;
    project?: string;
  },
  type: "csv" | "excel"
) => {
  const sp = new URLSearchParams();
  sp.set("type", type);
  if (params.search) sp.set("search", params.search);
  if (params.po_status) sp.set("po_status", params.po_status);
  if (params.delivery_status) sp.set("delivery_status", params.delivery_status);
  if (params.project) sp.set("project", params.project);
  return `${base}inventory/api/v1/purchase-orders/export/?${sp.toString()}`;
};

const downloadExport = async (url: string, filename: string) => {
  const token = getToken();
  const userRole = getUserRole();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (userRole) headers["AccessRole"] = userRole;
  const res = await fetch(url, { headers, credentials: "same-origin" });
  if (!res.ok) throw new Error(res.statusText);
  const blob = await res.blob();
  const ext = url.includes("type=csv") ? "csv" : "xlsx";
  const fileURL = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = fileURL;
  link.download = `${filename}.${ext}`;
  link.click();
  window.URL.revokeObjectURL(fileURL);
};

const InventoryPurchaseOrdersListView = () => {
  const [emailModalPo, setEmailModalPo] = useState<{
    id: number;
    poNumber: string;
  } | null>(null);

  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    search: string;
    po_status: string;
    delivery_status: string;
    project: string;
  }>({
    page: 1,
    pageSize: 10,
    search: "",
    po_status: "",
    delivery_status: "",
    project: "",
  });

  const {
    data: purchaseOrders,
    isLoading,
    isFetching,
  } = useGetPurchaseOrdersQuery(page);

  const [generateReportsPDFByUuid, { isLoading: isPdfLoading }] =
    useGenerateReportsPDFByUuidMutation();
  const [getPermitDownloadUids, { isLoading: isUidLoading }] =
    useGetPurchaseOrderDownloadUidsMutation();

  const { downloadState, setDownloadState, handleDownload } = usePdfDownloader({
    getPermitDownloadUids,
    generateReportsPDFByUuid,
  });

  // Export state and helpers
  const [exportLoading, setExportLoading] = useState(false);
  const apiBase =
    (typeof window !== "undefined" &&
      (window as any).__RUNTIME_CONFIG__?.REACT_APP_API_URL) ||
    "";

  const handleExport = useCallback(
    async (type: "csv" | "excel") => {
      if (!apiBase) return;
      setExportLoading(true);
      try {
        const url = buildPOExportUrl(
          apiBase,
          {
            search: page.search,
            po_status: page.po_status,
            delivery_status: page.delivery_status,
            project: page.project,
          },
          type
        );
        await downloadExport(url, "purchase_orders_export");
      } catch (e) {
        console.error("PO export failed", e);
      } finally {
        setExportLoading(false);
      }
    },
    [apiBase, page.search, page.po_status, page.delivery_status, page.project]
  );

  // Define PO status options for badge display
  const getPOStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      draft: { color: "secondary", label: "DRAFT" },
      submitted: { color: "warning", label: "SUBMITTED" },
      approved: { color: "success", label: "APPROVED" },
      rejected: { color: "danger", label: "REJECTED" },
      resubmit: { color: "info", label: "RESUBMIT" },
      Ordered: { color: "info", label: "ORDERED" },
      delivered: { color: "success", label: "DELIVERED" },
      pending: { color: "warning", label: "PENDING" },
      cancelled: { color: "danger", label: "CANCELLED" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  const getApprovalStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      Draft: { color: "secondary", label: "DRAFT" },
      Submitted: { color: "warning", label: "SUBMITTED" },
      Approved: { color: "success", label: "APPROVED" },
      Rejected: { color: "danger", label: "REJECTED" },
      Resubmit: { color: "info", label: "RESUBMIT" },
      Ordered: { color: "info", label: "ORDERED" },
      Delivered: { color: "success", label: "DELIVERED" },
      "Pending Approval" : { color: "warning", label: "PENDING APPROVAL" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Define Columns for Table
  const columns = useMemo(
    () => [
      {
        Header: "Purchase Order No",
        accessor: "inventory_purchase_order_number",
        Cell: ({
          row: {
            original: { id, inventory_purchase_order_number },
          },
        }: any) => (
          <Link
            to={`/purchase-order-detail/${inventory_purchase_order_number}/`}
            className="text-primary d-inline-block edit-item-btn view-po-class"
          >
            {inventory_purchase_order_number || `PO_${id}`}
          </Link>
        ),
      },
      {
        Header: "PR Number",
        accessor: "purchase_request_number",
        Cell: (cell: { value: any }) => (
          <span className="fw-semibold">{cell.value || "N/A"}</span>
        ),
      },
      {
        Header: "Project",
        accessor: "project_name",
      },
      {
        Header: "Supplier",
        accessor: "supplier_name",
        Cell: (cell: { value: any }) => (
          <span>{cell.value || "N/A"}</span>
        ),
      },
      {
        Header: "PO Type",
        accessor: "is_stock_transfer_po",
        Cell: ({ row }: any) =>
          row?.original?.is_stock_transfer_po ? (
            <Badge color="info">Stock Transfer PO</Badge>
          ) : (
            <Badge color="light" className="text-dark border">
              Standard PO
            </Badge>
          ),
      },
      {
        Header: "PO Status",
        accessor: "po_status",
        Cell: (cell: { value: any }) => getPOStatusBadge(cell.value),
      },
      {
        Header: "Approval Status",
        accessor: "approval_status_name",
        Cell: (cell: { value: any }) => getApprovalStatusBadge(cell.value),
      },
      {
        Header: "Delivery Status",
        accessor: "delivery_status",
        Cell: (cell: { value: any }) => getPOStatusBadge(cell.value),
      },
      {
        Header: "Undeliverable",
        accessor: "has_undeliverable_services",
        Cell: (cell: { value: any }) =>
          cell.value ? <Badge color="danger">UNDELIVERABLE</Badge> : null,
      },
      {
        Header: "Total Items",
        accessor: "items_count",
        Cell: (cell: { value: any }) => (
          <Badge color="info" pill>
            {cell.value || 0}
          </Badge>
        ),
      },
      {
        Header: "Created Date",
        accessor: "created_at",
        Cell: ({ value }: any) =>
          value ? formatDateTime(value, "datetime") : "N/A",
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({
          row: {
            original: { id, po_status, inventory_purchase_order_number },
          },
        }: any) => (
          <div className="d-flex gap-2">
            <ul className="list-inline hstack gap-2 mb-0">
              {/* {(IsValidPermissions(["VIEW_PURCHASE_ORDER"]) ||
                IsValidPermissions(["VIEW_ALL_PURCHASE_ORDER"])) && (
                  <li className="list-inline-item edit" title="View">
                    <Link
                      to={`/purchase-order-detail/${inventory_purchase_order_number}/`}
                      className="text-primary d-inline-block edit-item-btn view-po-class"
                    >
                      <i className="ri-eye-fill fs-16"></i>
                    </Link>
                  </li>
                )} */}

              {IsValidPermissions(["DOWNLOAD_PURCHASE_REQUEST"]) && (
                <li
                  className="list-inline-item edit"
                  title={
                    po_status?.toLowerCase() === "cancelled"
                      ? "Download Cancellation PDF"
                      : "Download PO PDF"
                  }
                >
                  <Link
                    to={`#`}
                    className="text-primary d-inline-block edit-item-btn export-task-status-class"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownload(
                        inventory_purchase_order_number,
                        po_status?.toLowerCase() === "cancelled"
                      );
                    }}
                  >
                    <i className="ri-download-fill"></i>
                  </Link>
                </li>
              )}
              {["approved", "cancelled"].includes(
                po_status?.toLowerCase()
              ) && (
                <li
                  className="list-inline-item"
                  title={
                    po_status?.toLowerCase() === "cancelled"
                      ? "Send Cancellation Email"
                      : "Send Email"
                  }
                >
                  <Link
                    to={`#`}
                    className={`d-inline-block edit-item-btn send-po-email-class ${
                      po_status?.toLowerCase() === "cancelled"
                        ? "text-danger"
                        : "text-primary"
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setEmailModalPo({ id, poNumber: inventory_purchase_order_number });
                    }}
                  >
                    <i className="ri-mail-send-line"></i>
                  </Link>
                </li>
              )}
              {IsValidPermissions(["EDIT_PURCHASE_ORDERS"]) &&
                (po_status?.toLowerCase() !== "cancelled" ? (
                  <li className="list-inline-item edit" title="Edit">
                    <Link
                      to={`/purchase-order-detail/${inventory_purchase_order_number}`}
                      className="text-primary d-inline-block edit-item-btn edit-po-class"
                    >
                      <i className="ri-pencil-fill fs-16"></i>
                    </Link>
                  </li>
                ) : (
                  <li className="list-inline-item" title="Cancelled — cannot edit">
                    <i
                      className="ri-pencil-fill fs-16"
                      style={{ color: "gray" }}
                    ></i>
                  </li>
                ))}
            </ul>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".view-po-class"
        place="bottom"
        content="View"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".edit-po-class"
        place="bottom"
        content="Edit"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".delivery-po-class"
        place="bottom"
        content="Manage Delivery"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".send-po-email-class"
        place="bottom"
        content="Send PO Email"
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Purchase Orders" pageTitle="Inventory" />
          <Row>
            <Col lg={12}>
              <Card id="purchaseOrdersList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <Col sm>
                      <div>
                        <h5 className="card-title mb-0">
                          Purchase Orders List
                        </h5>
                      </div>
                    </Col>
                    <Col sm="auto">
                      <div>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            color="secondary"
                            caret
                            disabled={exportLoading}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-download-line align-bottom me-1"></i>
                            {exportLoading ? "Exporting…" : "Export"}
                          </DropdownToggle>
                          <DropdownMenu className="dropdown-menu-end">
                            <DropdownItem
                              onClick={() => handleExport("excel")}
                              disabled={exportLoading}
                            >
                              Export as Excel
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => handleExport("csv")}
                              disabled={exportLoading}
                            >
                              Export as CSV
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={purchaseOrders?.results || []}
                      isLoading={isFetching}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={page.pageSize}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap mb-0"
                      theadClass="table-light text-muted"
                      thClass="table-light text-muted"
                      isPurchaseOrderFilter={true}
                      // isPermitFilter={true}
                      SearchPlaceholder="Search for Purchase Orders..."
                      isPagination={false}
                      isServerPagination={true}
                      serverPaginationProps={{
                        count: purchaseOrders?.count || 0,
                        next: purchaseOrders?.next || null,
                        page_size: purchaseOrders?.page_size || 10,
                        previous: purchaseOrders?.previous || null,
                        current_page: purchaseOrders?.current_page || 1,
                        total_page: purchaseOrders?.total_page || 0,
                      }}
                      setPageState={setPageState}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DownloadModal
        isOpen={downloadState.isOpen}
        toggle={() => setDownloadState((prev) => ({ ...prev, isOpen: false }))}
        status={downloadState.status}
        permitNo={downloadState.permitNo}
        errorMessage={downloadState.errorMessage}
      />
      {emailModalPo && (
        <POEmailModal
          isOpen={!!emailModalPo}
          toggle={() => setEmailModalPo(null)}
          poId={emailModalPo.id}
          poNumber={emailModalPo.poNumber}
        />
      )}
    </React.Fragment>
  );
};

export default InventoryPurchaseOrdersListView;